import { useSyncExternalStore, useCallback } from 'react';
import { prefsStore, type Theme } from '@lib/prefsStore';

interface UseThemeResult {
  theme: Theme;
  isDark: boolean;
  setTheme: (next: Theme) => void;
  toggle: () => void;
}

/**
 * Reactive accessor for the theme preference. Backed by prefsStore
 * (localStorage-persisted). Components consuming this hook re-render when
 * the user toggles themes anywhere in the app.
 * The actual DOM class (.dark on <html>) is applied by AppProviders so that
 * Tailwind's class-based dark mode picks it up even for portal'd content
 * (Radix Dialog, Dropdown, etc.) that mounts outside the React subtree.
 */
export function useTheme(): UseThemeResult {
  const theme = useSyncExternalStore(
    (listener) => prefsStore.subscribe(listener), () => prefsStore.get().theme, () => prefsStore.get().theme,
  );

  const isDark = theme === 'dark';

  const setTheme = useCallback((next: Theme) => {
    prefsStore.setTheme(next);
  }, []);

  const toggle = useCallback(() => {
    prefsStore.toggleTheme();
  }, []);

  return { theme, isDark, setTheme, toggle };
}
